/* Dependencies */
import styled, { css } from 'styled-components';

/* Components */
import { Optioner, Breakpoints } from 'kinedu-react-components';
import { Button, Texter } from 'lego-dist';

/* Icons */
import { ReactComponent as EditIcon } from 'images/icons/editRound.svg';
import { ReactComponent as Cancel } from 'images/icons/cancel.svg';
import { ReactComponent as Save } from 'images/icons/save.svg';
import { ReactComponent as Arrow } from 'images/icons/sortingArrowDown.svg';
import { ReactComponent as DropdownBtn } from 'images/icons/delete_dots.svg';
import magnifyingGlass from 'images/icons/magnifyingGlass.svg';

export const StylesImageModalResultsUpdateMember = css`
  margin: 30px auto;
  width: 60px;
  height: auto;
`;

export const Container = styled.div.attrs(() => ({
  className: 'OrganizationView',
}))`
  & .Optioner-box::after {
    height: 2px;
  }
`;

export const Header = styled.div.attrs(() => ({
  className: 'OrganizationHeader',
}))`
  display: flex;
  align-items: center;

  height: 40px;
  width: 100%;

  border-bottom: 1px solid #dde4ea;
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 16px;
  font-weight: bold;
  padding: 16px 20px;

  ${({ theme }) => theme.breakPoints.tablet} {
    font-size: 18px;
    height: 64px;
    padding: 8px 16px;
  }
`;

export const Body = styled.div.attrs(() => ({ className: 'OrganizationBody' }))`
  ${({ theme }) => theme.breakPoints.tablet} {
    padding: 16px 32px;
  }
`;

export const OrgInfo = styled.div.attrs(() => ({
  className: 'OrganizationInfo',
}))`
  display: flex;
  align-items: center;
  margin: 10px 0 0 16px;

  .legoModalHeader {
    border: 3px solid red;
    background: red;
  }

  & form {
    display: flex;
    align-items: center;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    margin: 0;
  }
`;

interface OrganizationImageContainerProps {
  disabled?: boolean;
}

export const OrganizationImageContainer = styled.div.attrs(() => ({
  className: 'OrganizationImage',
}))<OrganizationImageContainerProps>`
  position: relative;

  .legoPhotoUploaderContainer {
    ${({ disabled = false }) => `
      ${
        disabled
          ? `
        pointer-events: none;
      `
          : ''
      }
    `}
    .legoPhotoUploaderDropZone {
      .legoAvatarContainer {
        .legoAvatarSkeleton {
          padding: 0;

          .legoAvatarPhoto {
            height: 52px;
            width: 52px;
            :after {
              color: #ffffff;
              background-color: #0086d8;
            }
          }
        }
      }
    }
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    .legoPhotoUploaderContainer {
      .legoPhotoUploaderDropZone {
        .legoAvatarContainer {
          .legoAvatarSkeleton {
            .legoAvatarPhoto {
              height: 80px;
              width: 80px;
            }
          }
        }
      }
    }
  }
`;

export const OrgName = styled.div.attrs(() => ({
  className: 'OrganizationName',
}))`
  word-break: break-all;
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 14px;
  font-weight: bold;
  margin: auto 10px auto 16px;
  max-width: 288px;

  ${({ theme }) => theme.breakPoints.tablet} {
    font-size: 18px;
  }
`;

export const Overlay = styled.div.attrs(() => ({ className: 'DarkOverlay' }))`
  background-color: ${({ theme }) => theme.colors.blackAlpha};

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  height: 100vh;
  width: 100vw;
  z-index: 10000;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;

export const OrgEditWrapperMobile = styled.div.attrs(() => ({
  className: 'OrganizationEditWrapperMobile',
}))`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  background-color: ${({ theme }) => theme.colors.white};
  padding: 40px 16px 16px 16px;
  width: 100%;
  z-index: 11000;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;

export const OrgEditWrapperDesktop = styled.div.attrs(() => ({
  className: 'OrganizationEditWrapperDesktop',
}))`
  display: none;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: flex;
    align-items: center;
    position: relative;
  }
`;

export const OrgNameCharCount = styled.div.attrs(() => ({
  className: 'OrganizationNameCharCount',
}))`
  color: ${({ theme }) => theme.colors.shadeMedium};
  font-size: 14px;
  position: absolute;
  top: 18px;
  right: 16px;

  ${({ theme }) => theme.breakPoints.tablet} {
    top: -20px;
    right: 74px;
  }
`;

export const OrgEditName = styled(Texter).attrs(() => ({
  className: 'OrganizationEditName',
}))`
  position: relative;

  > input {
    height: 40px;
    width: 100%;

    border: 1px solid ${({ theme }) => theme.colors.shadeMedium};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.shadeMediumDark} !important;
    font-size: 16px !important;
    font-weight: normal;
    line-height: 16px;
    margin-bottom: 25px;
    padding: 0 0 0 12px;

    &:focus {
      border: 1px solid ${({ theme }) => theme.colors.physicalLight30};
    }
  }

  & .texter-error {
    color: #ef4b48;
    font-size: 14px;
    font-weight: normal;

    position: absolute;
    top: 45px;
    left: 0;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    > input {
      margin: auto 10px auto 16px;
      width: 288px;
    }

    & .texter-error {
      left: 16px;
    }
  }
`;

export const EditBtn = styled(EditIcon).attrs(() => ({
  className: 'OrganizationNameEditButton',
}))`
  cursor: pointer;
  height: 24px;
  width: 24px;

  & .pen {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }

  &:hover .pen {
    fill: #85daed;
  }
`;

export const EditBtnImg = styled(EditIcon).attrs(() => ({
  className: 'OrganizationNameEditImageButton',
}))`
  cursor: pointer;
  height: 20px;
  width: 20px;

  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 10;

  & .pen {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }

  &:hover .pen {
    fill: #85daed;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    height: 24px;
    width: 24px;
  }
`;

export const EditActions = styled.div.attrs(() => ({
  className: 'OrganizationEditActions',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  ${({ theme }) => theme.breakPoints.tablet} {
    justify-content: flex-start;
  }
`;

export const EditCancelMobile = styled(Button).attrs(() => ({
  className: 'OrganizationEditCancelMobile',
}))`
  border: 1px solid ${({ theme }) => theme.colors.shadeMediumDark};
  color: ${({ theme }) => theme.colors.shadeMediumDark};

  height: 32px;
  width: 100%;
`;

export const EditCancel = styled(Cancel).attrs(() => ({
  className: 'OrganizationEditCancel',
}))`
  cursor: pointer;
  height: 20px;
  width: 20px;

  & .icon {
    fill: ${({ theme }) => theme.colors.shadeMediumDark};
  }
`;

export const EditSaveMobile = styled(Button).attrs(() => ({
  className: 'OrganizationEditSaveMobile',
}))`
  height: 32px;
  width: 100%;
`;

export const EditSaveDesktop = styled(Button).attrs(() => ({
  className: 'OrganizationEditSaveDesktop',
}))`
  padding: 0;
`;

export const EditSave = styled(Save).attrs(() => ({
  className: 'OrganizationEditSave',
}))`
  cursor: pointer;
  height: 24px;
  width: 24px;
`;

export const StyledOptioner = styled(Optioner).attrs(() => ({
  className: 'OrganizationSettingsTabs',
}))`
  width: 100%;

  @media only screen and (min-width: 600px) {
    width: max-content;

    & .Optioner-box {
      width: 160px;
    }
  }
`;

export const OrgTab = styled.div.attrs(() => ({
  className: 'OrganizationTabView',
}))`
  padding: 0 16px;
  width: 100%;

  ${({ theme }) => theme.breakPoints.tablet} {
    padding: 0;
  }
`;

export const CenterHeader = styled.div.attrs(() => ({
  className: 'OrganizationCenterHeader',
}))`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: 60px;
  width: 100%;
`;

export const CenterCount = styled.div.attrs(() => ({
  className: 'OrganizationCenterCount',
}))`
  display: none;

  color: ${({ theme }) => theme.colors.shadeDark};
  font-size: 16px;
  font-weight: bold;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: block;
  }
`;

export const CenterActions = styled.div.attrs(() => ({
  className: 'OrganizationCenterActions',
}))`
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.breakPoints.tablet} {
    width: min-content;
  }
`;

export const SearchBar = styled(Texter).attrs(() => ({
  className: 'OrganizationCenterSearch',
}))`
  margin-right: 16px;

  & .texter-input {
    border: 1px solid #acc2d3;
    border-radius: 20px;
    color: #8295a3;
    font-size: 14px;
    font-style: normal;
    font-weight: normal;
    padding-left: 16px;

    height: 40px;
    width: 100%;
  }

  & .texter-input::placeholder {
    color: #8295a3;
    font-style: normal;
    font-weight: normal;
    vertical-align: center;
  }

  ${Breakpoints.tablet} {
    & .texter-input {
      width: 180px;
    }
  }
`;

export const SearchIcon = styled.img.attrs(() => ({
  className: 'OrganizationCenterSearch',
  src: magnifyingGlass,
}))`
  height: 21px;
  right: 16px;
  position: absolute;
  top: 25%;
  width: 13px;
`;

export const AddCenterBtnMobile = styled(Button).attrs(() => ({
  className: 'OrganizationAddCenterButtonMobile',
  rounded: true,
  flat: true,
  fill: true,
  color: 'var(--cognitive-green)',
}))`
  font-size: 35px;
  height: 40px;
  width: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: none;
  }
`;

export const AddCenterBtn = styled(Button).attrs(() => ({
  className: 'OrganizationAddCenterButton',
  rounded: true,
  flat: true,
  fill: true,
  color: 'var(--cognitive-green)',
}))`
  display: none;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: block;
    min-width: 106px;
    padding: 8px 16px !important;
  }
`;

export const CenterList = styled.div.attrs(() => ({
  className: 'OrganizationCenterList',
}))`
  width: 100%;
`;

export const CenterListHeader = styled.div.attrs(() => ({
  className: 'OrganizationCenterListHeader',
}))`
  display: grid;
  grid-template-columns: repeat(14, 1fr);
  grid-template-rows: 40px;
  align-items: center;

  border-bottom: 1px solid ${({ theme }) => theme.colors.shadeMediumLight};
  color: ${({ theme }) => theme.colors.shadeDark};
  font-size: 14px;
  margin-bottom: 10px;
`;

export const CenterListHeaderName = styled.div.attrs(() => ({
  className: 'OrganizationCenterListHeaderName',
}))`
  grid-column: 1 / 11;

  display: flex;
  align-items: center;
`;

export const SortArrow = styled(Arrow).attrs(() => ({
  className: 'OrganizationCenterListUpArrow',
}))`
  height: 10px;
  width: 14px;

  margin-left: 20px;

  &:hover {
    cursor: pointer;
  }
`;

export const CenterListHeaderClassrooms = styled.div.attrs(() => ({
  className: 'OrganizationCenterClassrooms',
}))`
  display: none;
  grid-column: 11 / 15;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: block;
  }
`;

export const CenterItem = styled.div.attrs(() => ({
  className: 'OrganizationCenterListItem',
}))`
  display: grid;
  grid-template-columns: repeat(14, 1fr);
  grid-template-rows: 60px 30px;
  align-items: center;

  border: 1px solid ${({ theme }) => theme.colors.shadeMediumLight};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.shadeDark};
  margin-bottom: 10px;
  transition: 0.5s;

  ${({ theme }) => theme.breakPoints.tablet} {
    display: grid;
    grid-template-columns: repeat(14, 1fr);
    grid-template-rows: 64px;
    row-gap: 10px;
    align-items: center;
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.physicalLight30};
    color: ${({ theme }) => theme.colors.darkGray};
  }
`;

export const CenterName = styled.div.attrs(() => ({
  className: 'OrganizationCenterName',
}))`
  grid-column: 1 / 13;

  display: flex;
  align-items: center;

  font-size: 14px;
  font-weight: bold;
  padding: 0 12px;

  & p {
    margin-left: 16px;
    max-width: 170px;
    word-wrap: break-word;
  }
  cursor: pointer;

  ${({ theme }) => theme.breakPoints.tablet} {
    grid-column: 1 / 11;
    padding: 0 10px;
    font-size: 16px;

    & p {
      max-width: 320px;
    }
  }
`;

export const CenterImg = styled.img.attrs(() => ({
  className: 'OrganizationCenterImage',
  alt: 'Center Image',
}))`
  border-radius: 50%;
  object-fit: cover;
  overflow: hidden;
  height: 40px;
  width: 40px;
`;

export const ClassroomCount = styled.div.attrs(() => ({
  className: 'OrganizationCenterClassroomsCount',
}))`
  cursor: pointer;
  grid-column: 1 / 13;
  grid-row: 2 / 3;

  font-size: 14px;
  margin-left: 70px;

  ${({ theme }) => theme.breakPoints.tablet} {
    grid-column: 11 / 14;
    grid-row: 1 / 2;
    margin-left: 3px;
    height: 100%;
    display: flex;
    align-items: center;

    & span {
      display: none;
    }
  }
`;
ClassroomCount.displayName = 'OrganizationCenterClassroomsCount';

export const CenterDropdown = styled.div.attrs(() => ({
  className: 'OrganizationCenterDropdown',
}))`
  grid-column: 13 / 15;

  ${({ theme }) => theme.breakPoints.tablet} {
    grid-column: 14 / 15;

    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const ItemDropdown = styled.div.attrs({ className: 'ItemDropdown' })`
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  & span {
    color: ${({ theme }) => theme.colors.shadeDark};
    font-size: 14px;
    padding: 7px 15px;
    text-align: left;
    width: 100%;
  }

  & span:hover {
    background-color: #0086d810;
    color: ${({ theme }) => theme.colors.darkGray};
    cursor: pointer;
  }

  @media only screen and (max-width: 600px) {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    padding: 16px;
    width: 100%;
    z-index: 11000;
  }

  ${({ theme }) => theme.breakPoints.tablet} {
    border: 1px solid ${({ theme }) => theme.colors.shadeMediumLight};
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 #00000015;
    min-width: 157px;
    padding: 9px 0;
  }
`;

export const Dots = styled(DropdownBtn).attrs(() => ({
  className: 'ItemDropdownButton',
}))`
  height: 50px;
`;

export const CenterListEmpty = styled.div.attrs(() => ({
  className: 'OrganizationCenterListEmptyState',
}))`
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${({ theme }) => theme.colors.shadeMediumDark};
  font-size: 16px;
  margin: 100px auto;
`;

export const ConfigHeader = styled.div.attrs(() => ({
  className: 'OrganizationConfigHeader',
}))`
  height: 60px;
  width: 100;
  font-weight: bold;

  display: flex;
  align-items: center;

  color: ${({ theme }) => theme.colors.shadeDark};
  font-size: 16px;
  font-weight: bold;
`;

export const ConfigBody = styled.div.attrs(() => ({
  className: 'OrganizationConfigBody',
}))`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  grid-gap: 30px;

  position: relative;

  ${({ theme }) => theme.breakPoints.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const ConfigItem = styled.div.attrs(() => ({
  className: 'OrganizationConfigItem',
}))``;

export const Label = styled.div.attrs(() => ({
  className: 'OrganizationConfigItemLabel',
}))`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 14px;
  height: 32px;
  margin-bottom: 5px;

  display: flex;
  align-items: center;
`;

export const IndividualCenterConfigWrapper = styled.div.attrs(() => ({
  className: 'OrganizationIndividualCenterWrapper',
}))`
  margin: 16px 0 20px;

  ${({ theme }) => theme.breakPoints.tablet} {
    margin: 40px 0;
  }
`;

export const IndividualCenterConfigHeader = styled.div.attrs(() => ({
  className: 'OrganizationIndividualCenterHeader',
}))`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;

  ${({ theme }) => theme.breakPoints.tablet} {
    font-size: 16px;
  }
`;

export const IndividualCenterConfig = styled.div.attrs(() => ({
  className: 'OrganizationIndividualCenter',
}))`
  color: ${({ theme }) => theme.colors.shadeDark};
  font-size: 12px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => theme.breakPoints.tablet} {
    font-size: 14px;
  }
`;

export const SaveBtn = styled.div.attrs(() => ({
  className: 'OrganizationConfigSaveButtonWrapper',
}))`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const SwitcherWrapperDisabledStyles = css`
  .legoSwitcherLabel {
    background-color: #8295a3;
  }

  .legoSwitcherInput:checked + .slider,
  .slider {
    transform: translateX(0);
  }

  * {
    pointer-events: none;
  }
`;

export const SwitcherWrapper = styled.div.attrs(() => ({
  className: 'OrganizationConfigSwitcherWrapper',
}))<any>`
  padding-left: 38px;
  ${({ disabled }) => disabled && SwitcherWrapperDisabledStyles};
`;

export const DeactivateOverlay = styled.div.attrs(() => ({
  className: 'OrganizationConfigDeactivateItems',
}))`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;

  background-color: #ffffff80;
  z-index: 1;
`;

export const CoverLetter = styled.div.attrs(() => ({
  className: 'CoverLetter',
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  border-radius: 50%;
  object-fit: cover;
  overflow: hidden;
  height: 40px;
  width: 40px;
  min-width: 40px;
  color: #fff;
  background-color: #fbc090;
  text-transform: uppercase;
`;

export const Warning = styled.div.attrs(() => ({
  className: 'OrganizationConfigWarning',
}))`
  color: #f77b5e;
  font-size: 14px;
  margin-top: 20px;
`;

export const WrapperSelecter = styled.div.attrs(() => ({
  className: 'WrapperSelecterMoveDirector',
}))`
  display: block;
  position: relative;
  margin-top: 5px;

  .legoSelecterOptionsList .legoSelecterOptionItem {
    display: flex;
    align-items: center;
  }

  .legoSelecterOptionsContainer {
    margin-top: 0;
    max-height: 200px;
    overflow-y: auto;
    padding: 0;
  }

  .legoSelecterCheckbox[type='checkbox'] {
    position: relative;
    left: 10px;
  }

  .legoSelecterOptionIndicator {
    top: 12px;
  }

  .legoSelecterOptionsList {
    margin-top: 0;
  }
`;

export const ExportDataOption = styled.span<any>`
  color: ${({ isDisabled }) => (isDisabled ? '#ACC2D3' : '#4A4A4A')} !important;
  cursor: ${({ isDisabled }) =>
    isDisabled ? 'not-allowed' : 'default'} !important;
`;
